import { insertTable } from '@platejs/table';
import {
  TableCellHeaderPlugin,
  TableCellPlugin,
  TablePlugin,
  TableRowPlugin,
} from '@platejs/table/react';
import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  PButton,
  PInputNumber,
  PTable,
  PTableBody,
  PTableCell,
  PTableHead,
  PTableHeadCell,
  PTableHeadRow,
  PTableRow,
} from '@porsche-design-system/components-react';
import { useEditorState, type PlateElementProps } from 'platejs/react';
import {
  Children,
  isValidElement,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';

import TableIcon from '../../../../../icons/table.svg?react';
import { Popover } from '../../../Popover';
import { ToolbarButton } from '../../ToolbarButton';
import { PopoverActionContainer, PopoverContent } from './shared';

const TableElementWrapper = styled('div', {
  base: {
    overflowX: 'auto',
    width: '100%',
  },
});

const TableCellHeaderElement = ({
  attributes,
  children,
}: PlateElementProps) => {
  return (
    <PTableHeadCell {...attributes} multiline>
      {children}
    </PTableHeadCell>
  );
};

const isHeaderRowChildren = (children: ReactNode) => {
  const cells = Children.toArray(children).filter(isValidElement);

  return (
    cells.length > 0 &&
    cells.every((child) => child.type === TableCellHeaderElement)
  );
};

const TableElement = ({ attributes, children }: PlateElementProps) => {
  const rows = Children.toArray(children);
  const headerRows: ReactNode[] = [];
  const bodyRows: ReactNode[] = [];
  let inBody = false;

  rows.forEach((row) => {
    const rowElement = row as ReactElement<{
      children?: ReactNode;
    }>;

    if (
      !inBody &&
      isValidElement(row) &&
      isHeaderRowChildren(rowElement.props.children)
    ) {
      headerRows.push(row);
      return;
    }

    inBody = true;
    bodyRows.push(row);
  });

  return (
    <TableElementWrapper>
      <PTable {...attributes}>
        {headerRows.length > 0 && <PTableHead>{headerRows}</PTableHead>}
        {bodyRows.length > 0 && <PTableBody>{bodyRows}</PTableBody>}
      </PTable>
    </TableElementWrapper>
  );
};

const TableRowElement = ({ attributes, children }: PlateElementProps) => {
  const Component = isHeaderRowChildren(children) ? PTableHeadRow : PTableRow;

  return <Component {...attributes}>{children}</Component>;
};

const TableCellElement = ({ attributes, children }: PlateElementProps) => {
  return (
    <PTableCell {...attributes} multiline>
      {children}
    </PTableCell>
  );
};

export const tablePlugin = TablePlugin.withComponent(TableElement);
export const tableRowPlugin = TableRowPlugin.withComponent(TableRowElement);
export const tableCellPlugin = TableCellPlugin.withComponent(TableCellElement);
export const tableCellHeaderPlugin = TableCellHeaderPlugin.withComponent(
  TableCellHeaderElement,
);

const sanitizeTableSize = (value: string, fallback: number) => {
  const parsedValue = Number.parseInt(value, 10);

  if (!Number.isFinite(parsedValue)) {
    return fallback;
  }

  return Math.max(1, parsedValue);
};

export const TableToolbarButtons = ({
  insertTableLabel = 'Insert table',
  columnLabel = 'Columns',
  rowLabel = 'Rows',
}: {
  insertTableLabel?: string;
  columnLabel?: string;
  rowLabel?: string;
}) => {
  const editor = useEditorState();
  const [isOpen, setIsOpen] = useState(false);
  const [columnCount, setColumnCount] = useState('2');
  const [rowCount, setRowCount] = useState('2');

  return (
    <Popover
      open={isOpen}
      onChange={setIsOpen}
      target={
        <ToolbarButton active={isOpen} tooltip={insertTableLabel} type="button">
          <TableIcon />
        </ToolbarButton>
      }
    >
      <PopoverContent>
        <PInputNumber
          controls
          label={columnLabel}
          name="tableColumns"
          min={1}
          step={1}
          value={columnCount}
          onClick={(event: MouseEvent<HTMLElement>) => event.stopPropagation()}
          onInput={(event: CustomEvent<InputEvent>) => {
            setColumnCount(
              (event.target as HTMLInputElement & { value?: string }).value ??
                '',
            );
          }}
          onBlur={() => {
            setColumnCount(String(sanitizeTableSize(columnCount, 2)));
          }}
        />

        <PInputNumber
          controls
          label={rowLabel}
          name="tableRows"
          min={1}
          step={1}
          value={rowCount}
          onClick={(event: MouseEvent<HTMLElement>) => event.stopPropagation()}
          onInput={(event: CustomEvent<InputEvent>) => {
            setRowCount(
              (event.target as HTMLInputElement & { value?: string }).value ??
                '',
            );
          }}
          onBlur={() => {
            setRowCount(String(sanitizeTableSize(rowCount, 2)));
          }}
        />

        <PopoverActionContainer>
          <PButton
            type="button"
            onClick={() => {
              insertTable(editor, {
                colCount: sanitizeTableSize(columnCount, 2),
                rowCount: sanitizeTableSize(rowCount, 2),
              });
              setIsOpen(false);
              editor.tf.focus();
            }}
          >
            {insertTableLabel}
          </PButton>
        </PopoverActionContainer>
      </PopoverContent>
    </Popover>
  );
};
